// API service for raid boss management
import { skillEffectRelationApi } from "./skillEffectRelations";
import { supabase } from '../lib/supabase';
import { 
  BossInfo, 
  BossSkill, 
  BossEffect, 
  BossSkillEffectRelation,
  ReferenceData,
  InfSkillType,
  InfTargetType,
  InfSkillArea,
  InfInterruptible,
  InfEffectType,
  InfEffectAffect,
  InfEffectDispel,
  InfDurationType,
  InfPowerType,
  InfDifficulty,
  InfMechanismType,
  InfStat,
  InfCalculationMethod,
  CopyOptions,
  TriggerDefinitionV2
} from '../types';

// Boss API
const bossApi = {
  async getAll(): Promise<BossInfo[]> {
    const { data, error } = await supabase
      .from('boss_info_renewed')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('❌ boss_info_renewed 쿼리 실패:', error);
      throw error;
    }
    
    
    // 아동바동이 있는지 확인
    const adongBadong = (data || []).find(boss => boss.boss_name.includes('아동바동'));
    if (adongBadong) {
      } else {
      }
    
    return data || [];
  },

  async getByCode(boss_code: string): Promise<BossInfo | null> {
    const { data, error } = await supabase
      .from('boss_info_renewed')
      .select('*')
      .eq('boss_code', boss_code)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    
    return data;
  },

  async create(boss: Omit<BossInfo, 'created_at' | 'updated_at'>): Promise<BossInfo> {
    // raid_entity_renewed에 삽입
    const entityData = {
      entity_code: boss.boss_code,
      entity_name: boss.boss_name,
      entity_type: 'BOSS',
      description: boss.description,
      max_hp: boss.max_hp,
      phase_count: boss.phase_count,
      difficulty: boss.difficulty,
      game_version: boss.game_version
    };
    
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .insert(entityData)
      .select()
      .single();
    
    if (error) throw error;
    
    // BossInfo 형식으로 변환
    return {
      id: data.id,
      boss_code: data.entity_code,
      boss_name: data.entity_name,
      description: data.description,
      max_hp: data.max_hp,
      phase_count: data.phase_count,
      difficulty: data.difficulty,
      game_version: data.game_version,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  },

  async update(boss_code: string, boss: Partial<BossInfo>): Promise<BossInfo> {
    // BossInfo에서 entity 형식으로 변환
    const entityUpdate: any = {};
    if (boss.boss_name !== undefined) entityUpdate.entity_name = boss.boss_name;
    if (boss.description !== undefined) entityUpdate.description = boss.description;
    if (boss.max_hp !== undefined) entityUpdate.max_hp = boss.max_hp;
    if (boss.phase_count !== undefined) entityUpdate.phase_count = boss.phase_count;
    if (boss.difficulty !== undefined) entityUpdate.difficulty = boss.difficulty;
    if (boss.game_version !== undefined) entityUpdate.game_version = boss.game_version;
    
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .update(entityUpdate)
      .eq('entity_code', boss_code)
      .eq('entity_type', 'BOSS')
      .select()
      .single();
    
    if (error) throw error;
    
    // BossInfo 형식으로 변환
    return {
      id: data.id,
      boss_code: data.entity_code,
      boss_name: data.entity_name,
      description: data.description,
      max_hp: data.max_hp,
      phase_count: data.phase_count,
      difficulty: data.difficulty,
      game_version: data.game_version,
      created_at: data.created_at,
      updated_at: data.updated_at
    };
  },

  async delete(boss_code: string): Promise<void> {
    const { error } = await supabase
      .from('raid_entity_renewed')
      .delete()
      .eq('entity_code', boss_code)
      .eq('entity_type', 'BOSS');
    
    if (error) throw error;
  }
};

// Skill API
const skillApi = {
  async getAll(): Promise<BossSkill[]> {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .select('*')
      .order('boss_code', { ascending: true })
      .order('order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  async getByBoss(boss_code: string): Promise<BossSkill[]> {
    
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .select('*')
      .or(`boss_code.eq.${boss_code},boss_code.eq.COMMON`)
      .order('order', { ascending: true });
    
    if (error) {
      console.error('❌ Error fetching skills:', error);
      throw error;
    }
    
    return data || [];
  },

  async create(skill: Omit<BossSkill, 'id' | 'created_at' | 'updated_at'>): Promise<BossSkill> {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .insert(skill)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(key: string, skill: Partial<BossSkill>): Promise<BossSkill> {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .update(skill)
      .eq('key', key)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(key: string): Promise<void> {
    const { error } = await supabase
      .from('boss_skill_renewed')
      .delete()
      .eq('key', key);
    
    if (error) throw error;
  }
};

// Effect API
const effectApi = {
  async getAll(): Promise<BossEffect[]> {
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .order('boss_code', { ascending: true })
      .order('order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  async getByBoss(boss_code: string): Promise<BossEffect[]> {
    
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .or(`boss_code.eq.${boss_code},boss_code.eq.COMMON,boss_code.is.null`)
      .order('order', { ascending: true });
    
    if (error) {
      console.error('❌ Error fetching effects:', error);
      throw error;
    }
    
    return data || [];
  },

  async create(effect: Omit<BossEffect, 'id' | 'created_at' | 'updated_at'>): Promise<BossEffect> {
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .insert(effect)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(key: string, effect: Partial<BossEffect>): Promise<BossEffect> {
    // key 필드와 읽기 전용 필드들을 업데이트 페이로드에서 제거
    const { key: _, id: effectId, created_at, updated_at, ...updateData } = effect as any;
    
    // 빈 객체인지 확인
    if (Object.keys(updateData).length === 0) {
      throw new Error('업데이트할 데이터가 없습니다');
    }
    
    console.log('🔍 효과 업데이트 시도:', key);
    
    // ID가 있으면 ID로 업데이트 (더 안정적)
    if (effectId) {
      const { data, error } = await supabase
        .from('boss_effect_renewed')
        .update(updateData)
        .eq('id', effectId)
        .select()
        .single();
      
      if (error) {
        console.error('❌ ID 기반 업데이트 실패:', error);
      } else {
        return data;
      }
    }
    
    // ID가 없으면 먼저 key로 조회해서 ID를 가져온 다음 ID로 업데이트
    const { data: findData, error: findError } = await supabase
      .from('boss_effect_renewed')
      .select('id')
      .eq('key', key)
      .single();
    
    if (findError || !findData) {
      console.error('❌ key로 레코드를 찾을 수 없습니다:', key);
      console.error('❌ 조회 에러:', findError);
      
      // 디버깅을 위해 모든 COMMON 효과 확인
      const { data: commonEffects } = await supabase
        .from('boss_effect_renewed')
        .select('key, name')
        .eq('boss_code', 'COMMON')
        .like('key', '%instant_damage%');
      
      console.log('📋 COMMON의 instant_damage 효과들:', commonEffects);
      
      throw new Error(`효과를 찾을 수 없습니다: ${key}`);
    }
    
    // ID로 업데이트
    console.log('🔄 ID로 업데이트 시도:', findData.id);
    console.log('📝 업데이트할 데이터:', updateData);
    
    // 업데이트 전 현재 데이터 확인
    const { data: beforeData } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .eq('id', findData.id)
      .single();
    
    console.log('📋 업데이트 전 데이터:', beforeData);
    
    const { data: updateResult, error: updateError, count } = await supabase
      .from('boss_effect_renewed')
      .update(updateData)
      .eq('id', findData.id)
      .select();
    
    if (updateError) {
      console.error('❌ UPDATE 쿼리 자체가 실패:', updateError);
      throw updateError;
    }
    
    console.log('🔍 UPDATE 결과:', { updateResult, count });
    
    // 업데이트 후 데이터 다시 조회
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .eq('id', findData.id)
      .single();
    
    if (error) {
      console.error('❌ 업데이트 후 조회 실패:', error);
      throw error;
    }
    
    console.log('📋 업데이트 후 데이터:', data);
    console.log('✅ 업데이트 성공');
    
    // 데이터 변경 확인
    if (beforeData && data) {
      const changes = Object.keys(updateData).filter(key => 
        beforeData[key] !== data[key]
      );
      if (changes.length === 0) {
        console.warn('⚠️ 경고: 실제로 변경된 필드가 없습니다!');
      } else {
        console.log('✅ 변경된 필드:', changes);
      }
    }
    
    return data;
  },

  async delete(key: string): Promise<void> {
    const { error } = await supabase
      .from('boss_effect_renewed')
      .delete()
      .eq('key', key);
    
    if (error) throw error;
  }
};


// Reference Data API
const referenceApi = {
  // Trigger V3 관련 참조 데이터
  async fetchListenerTypes() {
    const { data, error } = await supabase.from('inf_listener_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchEventTypes() {
    const { data, error } = await supabase.from('inf_event_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchParticipantTypes() {
    const { data, error } = await supabase.from('inf_participant_role_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchConditionOperators() {
    const { data, error } = await supabase.from('inf_condition_operator').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchActionTypes() {
    const { data, error } = await supabase.from('inf_action_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchValueCompareOperators() {
    const { data, error } = await supabase.from('inf_value_compare_operator_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchBossPropertyCodes() {
    const { data, error } = await supabase.from('inf_boss_property_code_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  // 스킬 관련 참조 데이터
  async fetchSkillTypes() {
    const { data, error } = await supabase.from('inf_skill_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchTargetTypes() {
    const { data, error } = await supabase.from('inf_target_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchSkillAreas() {
    const { data, error } = await supabase.from('inf_skill_area_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchInterruptibles() {
    const { data, error } = await supabase.from('inf_interruptible_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  // 효과 관련 참조 데이터
  async fetchEffectTypes() {
    const { data, error } = await supabase.from('inf_effect_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },


  async fetchEffectAffects() {
    const { data, error } = await supabase.from('inf_effect_affect_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchEffectDispels() {
    const { data, error } = await supabase.from('inf_effect_dispel_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchDurationTypes() {
    const { data, error } = await supabase.from('inf_duration_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchPowerTypes() {
    const { data, error } = await supabase.from('inf_power_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchStats() {
    const { data, error } = await supabase.from('inf_stat_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  async fetchCalculationMethods() {
    const { data, error } = await supabase.from('inf_calculation_method_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  // 기타 참조 데이터
  async fetchDifficulties() {
    // 난이도 테이블이 없으므로 빈 배열 반환
    return [];
  },

  async fetchMechanismTypes() {
    const { data, error } = await supabase.from('inf_mechanism_type_renewed').select('*').order('sort_order');
    if (error) throw error;
    return data || [];
  },

  // 스킬/효과 데이터
  async fetchSkills() {
    const { data, error } = await supabase
      .from('boss_skill_renewed')
      .select('*')
      .order('boss_code', { ascending: true })
      .order('order', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async fetchEffects() {
    const { data, error } = await supabase
      .from('boss_effect_renewed')
      .select('*')
      .order('boss_code', { ascending: true })
      .order('order', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async fetchEntities() {
    const { data, error } = await supabase
      .from('raid_entity_renewed')
      .select('*')
      .order('entity_name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  // 레거시 호환성을 위한 getAllReferenceData (deprecated)
  async getAllReferenceData(): Promise<ReferenceData> {
    console.warn('getAllReferenceData is deprecated. Use individual fetch functions instead.');
    try {
      const [
        skillTypes,
        targetTypes,
        skillAreas,
        interruptibles,
        effectTypes,
        effectAffects,
        effectDispels,
        durationTypes,
        powerTypes,
        stats,
        calculationMethods,
        difficulties,
        mechanismTypes
      ] = await Promise.all([
        this.fetchSkillTypes(),
        this.fetchTargetTypes(),
        this.fetchSkillAreas(),
        this.fetchInterruptibles(),
        this.fetchEffectTypes(),
        this.fetchEffectAffects(),
        this.fetchEffectDispels(),
        this.fetchDurationTypes(),
        this.fetchPowerTypes(),
        this.fetchStats(),
        this.fetchCalculationMethods(),
        this.fetchDifficulties(),
        this.fetchMechanismTypes()
      ]);

      return {
        skillTypes,
        targetTypes,
        skillAreas,
        interruptibles,
        effectTypes,
        effectAffects,
        effectDispels,
        durationTypes,
        powerTypes,
        stats,
        calculationMethods,
        difficulties,
        mechanismTypes
      };
    } catch (error) {
      console.error('Error loading reference data:', error);
      throw error;
    }
  }
};

// Utility API
const utilityApi = {
  async generateKey(type: 'skill' | 'effect' | 'trigger', boss_code: string, name: string): Promise<string> {
    // 보스 코드와 이름에서 키 생성
    const prefix = boss_code === 'COMMON' ? 'CMN' : boss_code.substring(0, 3).toUpperCase();
    const namePart = name.replace(/[^a-zA-Z0-9가-힣]/g, '').substring(0, 10);
    const typePrefix = type === 'skill' ? 'SK' : type === 'effect' ? 'EF' : 'TRG';
    const timestamp = Date.now().toString().substring(-4);
    
    return `${prefix}_${typePrefix}_${namePart}_${timestamp}`;
  },

  async copyResource(options: {
    sourceKey: string;
    targetBossCode: string;
    resourceType: 'skill' | 'effect' | 'trigger';
    copyRelations?: boolean;
  }): Promise<{ newKey: string }> {
    const { sourceKey, targetBossCode, resourceType, copyRelations = true } = options;
    
    try {
      if (resourceType === 'skill') {
        // 원본 스킬 가져오기
        const sourceSkills = await skillApi.getAll();
        const sourceSkill = sourceSkills.find(s => s.key === sourceKey);
        if (!sourceSkill) throw new Error('원본 스킬을 찾을 수 없습니다');
        
        // 새 스킬 생성
        const newKey = await utilityApi.generateKey('skill', targetBossCode, sourceSkill.name);
        const { id, created_at, updated_at, ...skillData } = sourceSkill;
        const newSkill = {
          ...skillData,
          key: newKey,
          boss_code: targetBossCode,
          name: `${sourceSkill.name} (복사본)`,
        };
        
        await skillApi.create(newSkill);
        
        // 관계 복사
        if (copyRelations) {
          const relations = await skillEffectRelationApi.getBySkill(sourceSkill.name);
          for (const rel of relations) {
            const { id, created_at, ...relData } = rel;
            const newRel = {
              ...relData,
              skill: newSkill.name,
            };
            await skillEffectRelationApi.create(newRel);
          }
        }
        
        return { newKey };
      } else if (resourceType === 'effect') {
        // 원본 효과 가져오기
        const sourceEffects = await effectApi.getAll();
        const sourceEffect = sourceEffects.find(e => e.key === sourceKey);
        if (!sourceEffect) throw new Error('원본 효과를 찾을 수 없습니다');
        
        // 새 효과 생성
        const newKey = await utilityApi.generateKey('effect', targetBossCode, sourceEffect.name);
        const { id, created_at, updated_at, ...effectData } = sourceEffect;
        const newEffect = {
          ...effectData,
          key: newKey,
          boss_code: targetBossCode,
          name: `${sourceEffect.name} (복사본)`,
        };
        
        await effectApi.create(newEffect);
        
        return { newKey };
      } else {
        // 트리거 복사는 V2 시스템을 사용해야 함
        throw new Error('트리거 복사는 현재 지원되지 않습니다. V2 트리거 시스템을 사용하세요.');
      }
    } catch (error) {
      console.error('리소스 복사 실패:', error);
      throw error;
    }
  }
};

// ============================================
// 개선된 트리거 시스템 API
// ============================================

// 개선된 트리거 시스템 API
const improvedTriggerApi = {
  // 주체 타입 가져오기
  async getSubjectTypes() {
    const { data, error } = await supabase
      .from('inf_subject_type_advanced')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return data || [];
  },

  // 위치 조건 가져오기
  async getLocationConditions() {
    const { data, error } = await supabase
      .from('inf_location_condition_advanced')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return data || [];
  },

  // 연산자 가져오기
  async getOperators() {
    const { data, error } = await supabase
      .from('inf_operator_advanced')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return data || [];
  },

  // 타이밍 타입 가져오기
  async getTimingTypes() {
    const { data, error } = await supabase
      .from('inf_timing_type_advanced')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return data || [];
  },

  // 액션 타입 가져오기
  async getActionTypes() {
    const { data, error } = await supabase
      .from('trigger_action_type_advanced')
      .select('*')
      .order('code');
    
    if (error) throw error;
    return data || [];
  },

  // 조건 대상을 카테고리별로 가져오기
  async getConditionTargetsByCategory(category: string) {
    const { data, error } = await supabase
      .from('inf_condition_target_advanced')
      .select('*')
      .eq('category', category)
      .order('sort_order');
    
    if (error) throw error;
    return data || [];
  },

  // 모든 조건 대상 가져오기
  async getAllConditionTargets() {
    const { data, error } = await supabase
      .from('inf_condition_target_advanced')
      .select('*')
      .order('category', { ascending: true })
      .order('sort_order', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  // 단위 타입에 따른 연산자 가져오기  
  async getOperatorsByUnitType(unitType: string) {
    
    // 먼저 모든 연산자를 가져온 후 클라이언트에서 필터링
    // (inf_operator_advanced 테이블에 applies_to 필드가 없을 수 있음)
    const { data: allOperators, error } = await supabase
      .from('inf_operator_advanced')
      .select('*')
      .order('sort_order');
    
    
    if (error) {
      console.error('[getOperatorsByUnitType] Error:', error);
      throw error;
    }
    
    // 단위 타입에 따른 기본 연산자 매핑
    const operatorMapping: Record<string, string[]> = {
      'PERCENT': ['EQUAL', 'LESS_THAN', 'LESS_EQUAL', 'GREATER_THAN', 'GREATER_EQUAL', 'NOT_EQUAL', 'BETWEEN'],
      'NUMBER': ['EQUAL', 'LESS_THAN', 'LESS_EQUAL', 'GREATER_THAN', 'GREATER_EQUAL', 'NOT_EQUAL', 'BETWEEN'],
      'SECONDS': ['EQUAL', 'LESS_THAN', 'LESS_EQUAL', 'GREATER_THAN', 'GREATER_EQUAL', 'NOT_EQUAL', 'BETWEEN'],
      'COUNT': ['EQUAL', 'LESS_THAN', 'LESS_EQUAL', 'GREATER_THAN', 'GREATER_EQUAL', 'NOT_EQUAL', 'EVERY_N'],
      'BOOLEAN': ['EQUAL', 'NOT_EQUAL', 'EXISTS', 'NOT_EXISTS'],
      'EVENT': ['OCCURS', 'STARTS', 'ENDS'],
      'TILES': ['EQUAL', 'LESS_THAN', 'LESS_EQUAL', 'GREATER_THAN', 'GREATER_EQUAL', 'NOT_EQUAL'],
      'COORDINATES': ['EQUAL', 'NOT_EQUAL']
    };
    
    const allowedOperatorCodes = operatorMapping[unitType] || [];
    
    // 필터링된 연산자 반환
    const filteredOperators = (allOperators || []).filter(op => 
      allowedOperatorCodes.includes(op.operator_code)
    );
    
    return filteredOperators;
  },

  // 조건별 허용 연산자 및 메타데이터 가져오기
  async getConditionOperators(conditionTarget: string) {
    
    const { data, error } = await supabase
      .from('v_condition_operators_advanced')
      .select('*')
      .eq('condition_target', conditionTarget)
      .eq('is_allowed', true)
      .order('sort_order');
    
    
    if (error) {
      console.error('[getConditionOperators] Error:', error);
      throw error;
    }
    
    return data || [];
  },
  
  // 조건별 값 요구사항 가져오기
  async getConditionValueRequirement(conditionTarget: string) {
    const { data, error } = await supabase
      .from('inf_condition_value_advanced')
      .select('*')
      .eq('condition_target', conditionTarget)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error; // 데이터 없음은 무시
    return data;
  },

  // 트리거 생성
  async createTrigger(trigger: any, actions: any[]) {
    // trigger_id 생성
    const timestamp = Date.now().toString(36);
    const triggerId = `${trigger.boss_code}_TRIG_${timestamp}`.toUpperCase();
    
    const triggerWithId = {
      ...trigger,
      trigger_id: triggerId
    };

    const { data: triggerData, error: triggerError } = await supabase
      .from('trigger_definition_v2')
      .insert(triggerWithId)
      .select()
      .single();
    
    if (triggerError) throw triggerError;

    // 액션들 생성
    if (actions.length > 0) {
      const actionsWithTriggerId = actions.map((action, index) => ({
        trigger_id: triggerData.trigger_id,
        action_order: index + 1,
        ...action
      }));

      const { error: actionsError } = await supabase
        .from('trigger_action_relation_v2')
        .insert(actionsWithTriggerId);
      
      if (actionsError) throw actionsError;
    }

    return triggerData;
  },

  // 트리거 목록 가져오기
  async getTriggersByBoss(bossCode: string) {
    // V3 트리거 시스템 사용
    const { data, error } = await supabase
      .from('trigger_definition_v3')
      .select('*')
      .eq('entity_code', bossCode)
      .order('priority', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getTriggerActionsByBoss(bossCode: string) {
    // V3 트리거 시스템에서 보스의 모든 트리거 가져오기
    const { data: triggers, error: triggerError } = await supabase
      .from('trigger_definition_v3')
      .select('id')
      .eq('entity_code', bossCode);
    
    if (triggerError) throw triggerError;
    if (!triggers || triggers.length === 0) return [];
    
    const triggerIds = triggers.map(t => t.id);
    
    // 해당 트리거들의 액션 가져오기
    const { data, error } = await supabase
      .from('trigger_action_v3')
      .select('*')
      .in('trigger_id', triggerIds);
    
    if (error) throw error;
    return data || [];
  },

  // 트리거 삭제
  async deleteTrigger(triggerId: string) {
    const { error } = await supabase
      .from('trigger_definition_v2')
      .delete()
      .eq('trigger_id', triggerId);
    
    if (error) throw error;
  },

  // 트리거 업데이트
  async updateTrigger(triggerId: string, trigger: any, actions: any[]) {
    // 트리거 업데이트
    const { error: triggerError } = await supabase
      .from('trigger_definition_v2')
      .update({
        ...trigger,
        updated_at: new Date().toISOString()
      })
      .eq('trigger_id', triggerId);
    
    if (triggerError) throw triggerError;

    // 기존 액션 삭제
    const { error: deleteError } = await supabase
      .from('trigger_action_relation_v2')
      .delete()
      .eq('trigger_id', triggerId);
    
    if (deleteError) throw deleteError;

    // 새 액션들 추가
    if (actions.length > 0) {
      const actionsWithTriggerId = actions.map((action, index) => ({
        trigger_id: triggerId,
        action_order: index + 1,
        ...action
      }));

      const { error: actionsError } = await supabase
        .from('trigger_action_relation_v2')
        .insert(actionsWithTriggerId);
      
      if (actionsError) throw actionsError;
    }

    return { trigger_id: triggerId };
  }
};

// Export all APIs and individual fetch functions
export {
  bossApi,
  skillApi,
  effectApi,
  skillEffectRelationApi,
  // triggerApi는 제거됨 - improvedTriggerApi 사용
  improvedTriggerApi,
  referenceApi,
  utilityApi
};

